// ============================================================================
// SEARCE ASSETS - Types, Interfaces, and Constants
// ============================================================================

/** Predefined asset type dropdown options (matches backend AssetTypeEnum) */
export const ASSET_TYPE_OPTIONS = [
  'Accelerator', 'IP', 'Client POC', 'Internal POC', 'Internal Project',
] as const;

/** GTM category dropdown options (matches backend GTMCategoryEnum) */
export const GTM_CATEGORY_OPTIONS = [
  'Cloud Platform Modernization', 'Modern Product Engineering',
  'Data-to-Decision Intelligence', 'Cloud Security & Reliability Engineering',
  'Modern Work Ops', 'Modern Business Ops',
] as const;

/** Internal document category dropdown options (matches backend DocumentCategoryEnum) */
export const DOCUMENT_CATEGORY_OPTIONS = [
  'SOP', 'Technical Doc', 'Sales Training', 'Master Slide Deck', 'Other',
] as const;

/** Predefined cloud service suggestions for the autocomplete field */
export const CLOUD_SERVICE_OPTIONS = [
  'BigQuery', 'Vertex AI', 'Looker', 'Dataflow', 'GCP', 'AWS', 'Azure',
  'Cloud Run', 'Cloud Run Functions', 'IAP (Identity-Aware Proxy)', 'Firebase',
  'Firestore', 'GKE (Google Kubernetes Engine)', 'Compute Engine',
  'Storage Transfer Service', 'BigQuery Data Transfer Service',
  'GCS (Cloud Storage)', 'Dataproc', 'Spark (for incremental load)',
] as const;

/** Max length for card-facing short description (matches DB VARCHAR(160)) */
export const ASSET_SHORT_DESCRIPTION_MAX = 160;

/** A single internal documentation entry (category + url) */
export interface InternalDocumentLink {
  category: string;
  url: string;
}

/** A single external documentation entry (name + url) */
export interface ExternalDocumentLink {
  name: string;
  url: string;
}

/** Request payload for POST /assets */
export interface AssetCreateRequest {
  asset_name: string;
  short_description: string;
  asset_type: string;
  approx_start_date: string;
  asset_completion_date: string;
  gtm_category?: string | null;
  key_cloud_services: string[];
  problem_solved: string;
  primary_value_prop: string;
  target_customers_industries: string;
  internal_documentation: InternalDocumentLink[];
  external_documentation?: ExternalDocumentLink[];
  marketplace_listing_status: boolean;
  marketplace_url?: string | null;
  pricing_model?: string | null;
  gitlab_repo_url?: string | null;
  pr_code?: string | null;
  team_members: string[];
}

/** Single asset record returned by the API */
export interface AssetResponse {
  id: string;
  asset_code: string;
  submitted_by: string;
  submitted_at: string;
  asset_name: string;
  /** Null/empty for legacy assets created before this field existed */
  short_description?: string | null;
  asset_type: string | null;
  approx_start_date: string;
  asset_completion_date: string;
  gtm_category: string | null;
  key_cloud_services: string[];
  problem_solved: string;
  primary_value_prop: string;
  target_customers_industries: string;
  internal_documentation: InternalDocumentLink[];
  external_documentation: ExternalDocumentLink[] | null;
  marketplace_listing_status: boolean | null;
  marketplace_url: string | null;
  pricing_model: string | null;
  gitlab_repo_url: string | null;
  pr_code: string | null;
  team_members: string[];
  created_at: string;
  updated_at: string;
  /** From happier_data (active) by submitter email; optional when BQ unavailable or email unknown */
  submitter_business_unit?: string | null;
  submitter_sub_bu?: string | null;
}

/** Paginated list response from GET /assets */
export interface AssetListResponse {
  assets: AssetResponse[];
  total_count: number;
}

/** Local form state for the Submit Asset dialog */
export interface AssetFormState {
  asset_name: string;
  short_description: string;
  asset_type: string;
  approx_start_date: string;
  asset_completion_date: string;
  gtm_category: string;
  key_cloud_services: string[];
  problem_solved: string;
  primary_value_prop: string;
  target_customers_industries: string;
  internal_documentation: InternalDocumentLink[];
  external_documentation: ExternalDocumentLink[];
  marketplace_listing_status: boolean;
  marketplace_url: string;
  pricing_model: string;
  gitlab_repo_url: string;
  pr_code: string;
  team_members: string[];
}

/** Default empty form state */
export const EMPTY_ASSET_FORM: AssetFormState = {
  asset_name: '',
  short_description: '',
  asset_type: '',
  approx_start_date: '',
  asset_completion_date: '',
  gtm_category: '',
  key_cloud_services: [],
  problem_solved: '',
  primary_value_prop: '',
  target_customers_industries: '',
  internal_documentation: [],
  external_documentation: [],
  marketplace_listing_status: false,
  marketplace_url: '',
  pricing_model: '',
  gitlab_repo_url: '',
  pr_code: '',
  team_members: [],
};

/** Column definition for the assets table */
export interface AssetTableColumn {
  readonly id: string;
  readonly label: string;
  readonly width: number;
  readonly filterable: boolean;
}

/** Table column configuration */
export const ASSET_TABLE_COLUMNS: readonly AssetTableColumn[] = [
  { id: 'asset_code', label: 'Asset ID', width: 100, filterable: true },
  { id: 'asset_name', label: 'Asset Name', width: 180, filterable: true },
  { id: 'asset_type', label: 'Type', width: 120, filterable: true },
  { id: 'gtm_category', label: 'GTM Category', width: 180, filterable: true },
  { id: 'key_cloud_services', label: 'Cloud Services', width: 240, filterable: true },
  { id: 'marketplace_listing_status', label: 'Marketplace', width: 100, filterable: true },
  { id: 'submitter_business_unit', label: 'BU', width: 140, filterable: true },
  { id: 'submitter_sub_bu', label: 'SBU', width: 160, filterable: true },
  { id: 'submitted_by', label: 'Submitted By', width: 120, filterable: true },
  { id: 'submitted_at', label: 'Submitted At', width: 120, filterable: true },
] as const;

/** Sum of `ASSET_TABLE_COLUMNS[].width` — use as the table `minWidth` so `table-layout: fixed` honors column widths */
export const ASSET_TABLE_TOTAL_WIDTH = ASSET_TABLE_COLUMNS.reduce((sum, col) => sum + col.width, 0);

/** One cited source row from the assets AI /query response */
export interface AssetAiQuerySource {
  asset_id: string;
  asset_name?: string;
  asset_type?: string | null;
  gtm_category?: string | null;
  marketplace_url?: string | null;
  gitlab_repo_url?: string | null;
  content_preview?: string | null;
}

/** One message in `chat_history` for POST AI /query (conversation context) */
export interface AssetAiChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Request body for AI /query */
export interface AssetAiQueryRequest {
  user_question: string;
  /** Prior turns; keep short on the client (e.g. last N messages, truncated assistant text). */
  chat_history?: AssetAiChatHistoryMessage[];
  /**
   * Optional attachment: same POST /query as JSON, but sent as multipart/form-data
   * with field `file` when set.
   */
  file?: File | null;
}

/** Response from POST /assets/ai-query (proxied AI /query) */
export interface AssetAiQueryResponse {
  answer: string;
  asset_id?: string | null;
  /** Top-level asset codes (optional; order may match relevance) */
  assets?: string[];
  /** Rich citations; first-seen asset_id order is used for table filter (all unique IDs) */
  sources?: AssetAiQuerySource[];
}

/** Filter menu state for a single column */
export interface ColumnFilterMenuState {
  anchorEl: HTMLElement | null;
  selectedValues: string[];
}

/** Snackbar notification state */
export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}
